import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link2, Search, Plus, ArrowRight, Building2, Loader2, CheckCircle2 } from 'lucide-react';
import { EmptyState } from './_shared';
import { LinkSameInvoiceModal } from './LinkSameInvoiceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===================================================================
export function AddReceiptsToSheetModal({ token, onClose, onApply }) {
  const [items, setItems] = useState([]);
  const [itemsCache, setItemsCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [invoiceInputs, setInvoiceInputs] = useState({});
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const key = (item) => `${item.delivery_id}-${item.supplier_index}-${item.receipt_index}`;

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        const res = await fetch(`${API_URL}/api/invoice-matching/available-receipts?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          const fetched = data.items || [];
          setItems(fetched);
          setItemsCache(prev => {
            const next = { ...prev };
            fetched.forEach(it => { next[key(it)] = it; });
            return next;
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [token, search]);

  const updateInput = (k, field, value) => {
    setInvoiceInputs(prev => ({ ...prev, [k]: { ...prev[k], [field]: value } }));
  };

  const toggleSelect = (k) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const filledKeys = Object.entries(invoiceInputs)
    .filter(([k, v]) => v?.invoice_number?.trim() && itemsCache[k])
    .map(([k]) => k);

  const handleApply = () => {
    const out = filledKeys.map(k => {
      const item = itemsCache[k];
      const inp = invoiceInputs[k];
      return {
        delivery_id: item.delivery_id,
        supplier_index: item.supplier_index,
        receipt_index: item.receipt_index,
        supplier_name: item.supplier_name,
        receipt: item.receipt || {},
        invoice_number: inp.invoice_number.trim(),
        invoice_date: inp.invoice_date || null,
        amount: inp.amount ? parseFloat(inp.amount) : null,
        group_id: inp.group_id || null,
      };
    });
    if (out.length === 0) {
      toast.error('أدخل رقم فاتورة لاستلام واحد على الأقل');
      return;
    }
    onApply(out);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-stretch sm:items-center sm:justify-center sm:p-4" dir="rtl">
      <div className="bg-gray-50 w-full sm:max-w-3xl sm:rounded-2xl flex flex-col h-full sm:h-[88vh] overflow-hidden">
        <header className="bg-white border-b sticky top-0 z-10 px-4 py-3 flex items-center gap-3">
          <button onClick={onClose} className="w-10 h-10 -mr-2 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <ArrowRight className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-gray-900 text-base sm:text-lg">إضافة استلامات للكشف</h2>
            <p className="text-xs text-gray-500">يظهر فقط الاستلامات بدون فواتير مرتبطة</p>
          </div>
          <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full">{filledKeys.length} جاهز</span>
        </header>

        <div className="bg-white border-b px-4 py-3">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث برقم الاستلام، المورد، المشروع..."
              data-testid="add-receipts-search"
              className="w-full h-11 border rounded-lg pr-10 pl-4 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="لا توجد استلامات متاحة" subtitle="كل الاستلامات المعتمدة مرتبطة بفواتير" />
          ) : (
            items.map((item) => {
              const r = item.receipt || {};
              const k = key(item);
              const inp = invoiceInputs[k] || {};
              const filled = !!inp.invoice_number?.trim();
              return (
                <div key={k} className={`border-2 rounded-2xl p-3 ${filled ? 'border-emerald-300 bg-emerald-50/30' : selectedKeys.has(k) ? 'border-purple-400 bg-purple-50/30' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <label className="shrink-0 mt-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(k)}
                        onChange={() => toggleSelect(k)}
                        data-testid={`add-select-${k}`}
                        className="w-5 h-5 accent-purple-600"
                      />
                    </label>
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900 truncate">{item.supplier_name}</div>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                        <span>استلام #{r.receipt_number}</span>
                        <span>•</span>
                        <span>{r.receipt_date}</span>
                        {r.project_number && <><span>•</span><span>مشروع {r.project_number}</span></>}
                      </div>
                      {inp.group_id && (
                        <div className="text-[10px] text-purple-700 mt-0.5 flex items-center gap-1">
                          <Link2 className="w-3 h-3" /> مجموعة فاتورة موحَّدة
                        </div>
                      )}
                    </div>
                    {filled && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="رقم الفاتورة *"
                      value={inp.invoice_number || ''}
                      onChange={(e) => updateInput(k, 'invoice_number', e.target.value)}
                      data-testid={`add-invoice-num-${k}`}
                      className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm font-semibold focus:border-emerald-500 outline-none"
                    />
                    <input
                      type="date"
                      value={inp.invoice_date || ''}
                      onChange={(e) => updateInput(k, 'invoice_date', e.target.value)}
                      className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm focus:border-emerald-500 outline-none"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="المبلغ"
                      value={inp.amount || ''}
                      onChange={(e) => updateInput(k, 'amount', e.target.value)}
                      className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {selectedKeys.size > 0 && (
          <div className="border-t bg-purple-50 px-4 py-2.5 flex items-center gap-2">
            <span className="text-sm font-semibold text-purple-800">{selectedKeys.size} محدَّد</span>
            <div className="flex-1" />
            <button onClick={() => setSelectedKeys(new Set())} className="text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:bg-white">إلغاء</button>
            <button
              onClick={() => {
                if (selectedKeys.size < 2) { toast.error('اختر استلامَين أو أكثر'); return; }
                setLinkModalOpen(true);
              }}
              disabled={selectedKeys.size < 2}
              data-testid="add-link-same-invoice-btn"
              className="text-sm px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold disabled:opacity-50 flex items-center gap-1.5"
            >
              <Link2 className="w-4 h-4" /> ربط بنفس الفاتورة
            </button>
          </div>
        )}

        <footer className="border-t bg-white px-4 py-3 flex gap-2">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50">إلغاء</button>
          <button
            onClick={handleApply}
            disabled={filledKeys.length === 0}
            data-testid="add-apply-btn"
            className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> إضافة للكشف ({filledKeys.length})
          </button>
        </footer>
      </div>

      {linkModalOpen && (
        <LinkSameInvoiceModal
          count={selectedKeys.size}
          onClose={() => setLinkModalOpen(false)}
          onApply={({ invoice_number, invoice_date, amount }) => {
            const group_id = `grp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const keysArr = Array.from(selectedKeys);
            setInvoiceInputs(prev => {
              const next = { ...prev };
              keysArr.forEach((k, idx) => {
                next[k] = {
                  ...(next[k] || {}),
                  invoice_number,
                  invoice_date: invoice_date || '',
                  amount: idx === 0 ? (amount || '') : '',
                  group_id,
                };
              });
              return next;
            });
            setLinkModalOpen(false);
            setSelectedKeys(new Set());
            toast.success(`تم ربط ${keysArr.length} استلامات`);
          }}
        />
      )}
    </div>
  );
}

